/**
 * Status bar pill set (Faz 4.1 — OCS StatusPill parity).
 *
 * One component per OCS pill family, rendered through the shared IconToggle
 * chrome. Pills with a popup menu (Polar angle, Isolate, SelFilter, Units)
 * host a small fixed-position panel through the shared PillPopup wrapper;
 * right-clicking any pill area opens the StatusBarCustomize menu (every pill
 * is a check-marked row, OCS ToggleStatusPill).
 *
 * Conditional pills (Scale, SpaceMouse) stay registered in the customize
 * list once their owning phases (8/10) provide their store surfaces; the
 * annotative family (AnnoVisibility / AnnoAutoAdd / VpScaleSync) ships with
 * Faz 8 through the annotation scale store.
 */

import { useEffect, useState } from 'react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import {
    ArrowLeftRight,
    Crosshair,
    Eye,
    Filter,
    Glasses,
    ListChecks,
    Move3d,
    PanelTopClose,
    Pipette,
    Ruler,
    ScanLine,
    Waypoints,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import { useAppStore } from '../../state/store';
import { useStatusBarStore, type StatusPillId } from '../../state/statusBarStore';
import { useViewStore } from '../../state/commandAuxStores';
import { useSelectionStore } from '../../tools/selection';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useAnnotationScaleStore } from '../../state/annotationScaleStore';
import { useDraftingSettingsStore } from '../../state/useDraftingSettingsStore';
// Direct import (not the snapping barrel): keeps drei out of this chunk.
import { useSnapStore } from '../../tools/snapping/SnapManager';
import { getAnchorPoint } from '../../core/dynamicInput/dynamicInputEngine';
import { IconToggle } from './IconToggle';

/** Shared pill popup shell: fixed panel near the pill, closes on outside
 *  pointer-down or Escape. Dynamic position only (allowed inline style). */
function PillPopup({ anchor, onClose, children, width = 200 }: {
    anchor: { x: number; y: number } | null;
    onClose: () => void;
    children: React.ReactNode;
    width?: number;
}) {
    useEffect(() => {
        if (!anchor) return;
        const onDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('[data-pill-popup]')) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        // Defer so the opening click never closes the popup itself.
        const timer = window.setTimeout(() => {
            document.addEventListener('pointerdown', onDown, true);
            document.addEventListener('keydown', onKey);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [anchor, onClose]);

    if (!anchor) return null;
    return (
        <Box
            data-pill-popup
            position="fixed"
            left={Math.max(8, Math.min(anchor.x, (typeof window !== 'undefined' ? window.innerWidth : 800) - width - 12))}
            bottom="32px"
            w={`${width}px`}
            bg="bg.panel"
            border="1px solid"
            borderColor="border.base"
            borderRadius="sm"
            boxShadow="md"
            py={1}
            zIndex={30}
        >
            {children}
        </Box>
    );
}

/** A check-marked row inside a pill popup / customize menu. */
function PopupRow({ label, checked, onClick, disabled = false }: {
    label: string;
    checked?: boolean;
    onClick?: () => void;
    disabled?: boolean;
}) {
    return (
        <Flex
            as="button"
            w="100%"
            px={3}
            py={1}
            align="center"
            gap={2}
            fontSize="xs"
            color={disabled ? 'text.secondary' : 'text.primary'}
            opacity={disabled ? 0.5 : 1}
            cursor={disabled ? 'default' : 'pointer'}
            _hover={disabled ? {} : { bg: 'bg.active' }}
            onClick={disabled ? undefined : onClick}
            textAlign="left"
        >
            <Box w="12px" flexShrink={0} color="brand.500">{checked ? '✓' : ''}</Box>
            <Text isTruncated>{label}</Text>
        </Flex>
    );
}

// ---------------------------------------------------------------------------
// Coords — $COORDS 0/1/2 (static last-click / live absolute / live polar)
// ---------------------------------------------------------------------------

export function CoordsPill() {
    const { t } = useTranslation();
    const coordsMode = useStatusBarStore((s) => s.coordsMode);
    const cycleCoordsMode = useStatusBarStore((s) => s.cycleCoordsMode);
    const draftCursor = useSnapStore((s) => s.draftCursor);
    const [tick, setTick] = useState(0);

    // Polar mode reads the dynamic-input anchor (module ref — no store);
    // a light interval re-render keeps the readout moving without wiring a
    // per-pointermove store write into the drafting hot path.
    useEffect(() => {
        if (coordsMode !== 2) return;
        const id = window.setInterval(() => setTick((n) => (n + 1) % 1000), 120);
        return () => window.clearInterval(id);
    }, [coordsMode]);
    void tick;

    let text: string;
    if (coordsMode === 0) {
        // Static: the last committed click (dynamic-input anchor).
        const anchor = getAnchorPoint();
        text = anchor
            ? `${anchor.x.toFixed(2)}, ${anchor.y.toFixed(2)}, ${anchor.z.toFixed(2)}`
            : '0.00, 0.00, 0.00';
    } else if (coordsMode === 2) {
        // Polar: "{len} < {angle}" from the last click to the live cursor.
        const anchor: THREE.Vector3 | null = getAnchorPoint();
        if (anchor && draftCursor) {
            const dx = draftCursor.x - anchor.x;
            const dy = draftCursor.y - anchor.y;
            const len = Math.hypot(dx, dy);
            const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
            text = `${len.toFixed(2)} < ${angle.toFixed(1)}`;
        } else {
            text = '— < —';
        }
    } else {
        const p = draftCursor;
        text = p ? `${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}` : '0.00, 0.00, 0.00';
    }

    const modeLabel = t(`statusbar.coordsMode${coordsMode}`);
    return (
        <IconToggle
            icon={Crosshair}
            on={false}
            label={text}
            title={`${t('statusbar.pill.coords')} — ${modeLabel}`}
            onClick={cycleCoordsMode}
        />
    );
}

// ---------------------------------------------------------------------------
// Polar — F10 + right-click jump to DSETTINGS › Polar Tracking (Faz 11.13)
// ---------------------------------------------------------------------------

export function PolarPill({ anchor, setAnchor }: {
    anchor: { x: number; y: number } | null;
    setAnchor: (a: { x: number; y: number } | null) => void;
}) {
    const { t } = useTranslation();
    const polarOn = useViewStore((s) => s.polarOn);
    const polarAngle = useSystemVarsStore((s) => s.POLARANG);
    return (
        <>
            <IconToggle
                icon={Waypoints}
                on={polarOn}
                title={t('statusbar.pill.polar', { angle: polarAngle })}
                onClick={() => useViewStore.getState().set('polarOn', !polarOn)}
                // Right-click jumps to the DSETTINGS Polar Tracking tab
                // (Faz 11.13 — the pill/dialog merge point).
                onContextMenu={(e) => {
                    e.preventDefault();
                    useDraftingSettingsStore.getState().openAt('polarTracking');
                }}
            />
        </>
    );
}

// ---------------------------------------------------------------------------
// Isolate — active mode pill + right-click isolate popup (OCS isolate_popup)
// ---------------------------------------------------------------------------

export function IsolatePill({ anchor, setAnchor }: {
    anchor: { x: number; y: number } | null;
    setAnchor: (a: { x: number; y: number } | null) => void;
}) {
    const { t } = useTranslation();
    const mode = useAppStore((s) => s.view.hideIsolateMode);
    const cycle = (next?: 'isolate' | 'hide') => useAppStore.getState().cycleHideIsolate(next);
    return (
        <>
            <IconToggle
                icon={ScanLine}
                on={mode === 'isolate'}
                label={mode === 'isolate'
                    ? t('statusbar.isolateShort')
                    : mode === 'hide' ? t('statusbar.hiddenShort') : undefined}
                title={t('statusbar.pill.isolate')}
                onClick={() => cycle()}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setAnchor({ x: e.clientX, y: e.clientY });
                }}
                active={!!anchor}
            />
            <PillPopup anchor={anchor} onClose={() => setAnchor(null)} width={170}>
                <PopupRow
                    label={t('statusbar.isolateObjects')}
                    checked={mode === 'isolate'}
                    onClick={() => { cycle('isolate'); setAnchor(null); }}
                />
                <PopupRow
                    label={t('statusbar.hideObjects')}
                    checked={mode === 'hide'}
                    onClick={() => { cycle('hide'); setAnchor(null); }}
                />
                <PopupRow
                    label={t('statusbar.endIsolation')}
                    checked={!mode}
                    onClick={() => { useAppStore.getState().resetTemporaryHideIsolate(); setAnchor(null); }}
                />
            </PillPopup>
        </>
    );
}

// ---------------------------------------------------------------------------
// SelFilter — type + layer condition popup (OCS selection_filter_popup)
// ---------------------------------------------------------------------------

export function SelFilterPill({ anchor, setAnchor }: {
    anchor: { x: number; y: number } | null;
    setAnchor: (a: { x: number; y: number } | null) => void;
}) {
    const { t } = useTranslation();
    const filters = useSelectionStore((s) => s.filters);
    const setFilter = useSelectionStore((s) => s.setFilter);
    const resetFilters = useSelectionStore((s) => s.resetFilters);
    const entityTypes = useAppStore((s) => Object.keys(s.entities).length > 0
        ? Array.from(new Set(Object.values(s.entities).map((e) => e.type))).sort()
        : []);
    const layers = useAppStore((s) => Object.values(s.layers));
    const active = filters.entityTypes.length > 0 || filters.layers.length > 0;

    return (
        <>
            <IconToggle
                icon={Filter}
                on={active}
                title={t('statusbar.pill.selFilter')}
                onClick={() => {
                    if (active) resetFilters();
                    // Anchor near the bar's right edge (fixed popup pins to
                    // the bottom-right corner above the status bar).
                    else setAnchor({ x: (typeof window !== 'undefined' ? window.innerWidth : 800) - 240, y: 0 });
                }}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setAnchor({ x: e.clientX, y: e.clientY });
                }}
                active={!!anchor}
            />
            <PillPopup anchor={anchor} onClose={() => setAnchor(null)} width={220}>
                <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                    {t('statusbar.selFilterTypes')}
                </Box>
                <Box maxH="140px" overflowY="auto">
                    {entityTypes.length === 0 && (
                        <Box px={3} py={1} fontSize="xs" color="text.secondary">
                            {t('statusbar.selFilterEmpty')}
                        </Box>
                    )}
                    {entityTypes.map((type) => (
                        <PopupRow
                            key={type}
                            label={type}
                            checked={filters.entityTypes.includes(type)}
                            onClick={() => setFilter({
                                entityTypes: filters.entityTypes.includes(type)
                                    ? filters.entityTypes.filter((x) => x !== type)
                                    : [...filters.entityTypes, type],
                            })}
                        />
                    ))}
                </Box>
                <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                    {t('statusbar.selFilterLayers')}
                </Box>
                <Box maxH="140px" overflowY="auto">
                    {layers.map((layer) => (
                        <PopupRow
                            key={layer.id}
                            label={layer.name}
                            checked={filters.layers.includes(layer.id)}
                            onClick={() => setFilter({
                                layers: filters.layers.includes(layer.id)
                                    ? filters.layers.filter((x) => x !== layer.id)
                                    : [...filters.layers, layer.id],
                            })}
                        />
                    ))}
                </Box>
                <PopupRow label={t('statusbar.selFilterClear')} onClick={() => resetFilters()} />
            </PillPopup>
        </>
    );
}

// ---------------------------------------------------------------------------
// Units — LUNITS/LUPREC format popup (OCS units_popup)
// ---------------------------------------------------------------------------

const LUNITS_MODES: Array<{ value: number; key: 'statusbar.unitsScientific' | 'statusbar.unitsDecimal' | 'statusbar.unitsEngineering' | 'statusbar.unitsArchitectural' | 'statusbar.unitsFractional' }> = [
    { value: 1, key: 'statusbar.unitsScientific' },
    { value: 2, key: 'statusbar.unitsDecimal' },
    { value: 3, key: 'statusbar.unitsEngineering' },
    { value: 4, key: 'statusbar.unitsArchitectural' },
    { value: 5, key: 'statusbar.unitsFractional' },
];

export function UnitsPill({ anchor, setAnchor }: {
    anchor: { x: number; y: number } | null;
    setAnchor: (a: { x: number; y: number } | null) => void;
}) {
    const { t } = useTranslation();
    const lunits = useSystemVarsStore((s) => s.LUNITS);
    const luprec = useSystemVarsStore((s) => s.LUPREC);
    return (
        <>
            <IconToggle
                icon={Ruler}
                on={false}
                label={`${lunits}·${luprec}`}
                title={t('statusbar.pill.units')}
                onClick={() => setAnchor(anchor ? null : { x: (typeof window !== 'undefined' ? window.innerWidth : 800) - 230, y: 0 })}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setAnchor({ x: e.clientX, y: e.clientY });
                }}
                active={!!anchor}
            />
            <PillPopup anchor={anchor} onClose={() => setAnchor(null)} width={210}>
                <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                    {t('statusbar.unitsFormat')}
                </Box>
                {LUNITS_MODES.map((m) => (
                    <PopupRow
                        key={m.value}
                        label={t(m.key)}
                        checked={lunits === m.value}
                        onClick={() => useSystemVarsStore.getState().set('LUNITS', m.value)}
                    />
                ))}
                <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                    {t('statusbar.unitsPrecision')}
                </Box>
                <HStack px={3} pb={2} spacing={1} flexWrap="wrap">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((p) => (
                        <Box
                            key={p}
                            as="button"
                            px={1.5}
                            py={0.5}
                            fontSize="xs"
                            fontFamily="mono"
                            borderRadius="sm"
                            border="1px solid"
                            borderColor={luprec === p ? 'brand.500' : 'border.base'}
                            bg={luprec === p ? 'bg.active' : 'transparent'}
                            color="text.primary"
                            onClick={() => useSystemVarsStore.getState().set('LUPREC', p)}
                        >
                            {p}
                        </Box>
                    ))}
                </HStack>
            </PillPopup>
        </>
    );
}

// ---------------------------------------------------------------------------
// Annotative pills (Faz 8.12) — AnnoVisibility / AnnoAutoAdd / VpScaleSync
// backed by the annotation scale store (OCS annotative maintenance surface)
// ---------------------------------------------------------------------------

export function AnnotativePills({ hidden = [] }: { hidden?: StatusPillId[] }) {
    const { t } = useTranslation();
    const show = (id: StatusPillId) => !hidden.includes(id);
    const annoVisibility = useAnnotationScaleStore((s) => s.annoVisibility);
    const annoAutoAdd = useAnnotationScaleStore((s) => s.annoAutoAdd);
    const vpScaleSync = useAnnotationScaleStore((s) => s.vpScaleSync);
    const currentScale = useAnnotationScaleStore((s) => s.currentScale);
    return (
        <HStack spacing={1} flexShrink={0}>
            {show('annoVisibility') && (
                <IconToggle
                    icon={Glasses}
                    on={annoVisibility}
                    title={t('ui.cad.annotative.visibility')}
                    onClick={() => useAnnotationScaleStore.getState().setPill('annoVisibility', !annoVisibility)}
                />
            )}
            {show('annoAutoAdd') && (
                <IconToggle
                    icon={ScanLine}
                    on={annoAutoAdd}
                    title={t('ui.cad.annotative.autoAdd')}
                    onClick={() => useAnnotationScaleStore.getState().setPill('annoAutoAdd', !annoAutoAdd)}
                />
            )}
            {show('vpScaleSync') && (
                <IconToggle
                    icon={Crosshair}
                    on={vpScaleSync}
                    label={vpScaleSync ? currentScale : undefined}
                    title={t('ui.cad.annotative.vpSync')}
                    onClick={() => useAnnotationScaleStore.getState().setPill('vpScaleSync', !vpScaleSync)}
                />
            )}
        </HStack>
    );
}

// ---------------------------------------------------------------------------
// Customize menu — OCS ToggleStatusPill rows on status-bar right-click
// ---------------------------------------------------------------------------

/** Pill registry for the customize menu: id + translated label. Pills that
 *  wait for a later phase (Scale / Anno* / Vp / SpaceMouse) stay out of the
 *  menu until their store surfaces exist. */
type PillLabelKey =
    | 'statusbar.pill.coords' | 'statusbar.pill.ortho' | 'statusbar.pill.lwt'
    | 'statusbar.pill.polar' | 'statusbar.pill.dyn' | 'statusbar.pill.otrack'
    | 'statusbar.pill.osnap' | 'statusbar.pill.space' | 'statusbar.pill.units'
    | 'statusbar.pill.transparency' | 'statusbar.pill.isolate' | 'statusbar.pill.quickProps'
    | 'statusbar.pill.selFilter' | 'statusbar.pill.selCycle' | 'statusbar.pill.cleanScreen'
    | 'ui.cad.annotative.visibility' | 'ui.cad.annotative.autoAdd' | 'ui.cad.annotative.vpSync';

const STATUS_PILL_LABEL_KEYS: ReadonlyArray<{ id: StatusPillId; key: PillLabelKey }> = [
    { id: 'coords', key: 'statusbar.pill.coords' },
    { id: 'ortho', key: 'statusbar.pill.ortho' },
    { id: 'lwt', key: 'statusbar.pill.lwt' },
    { id: 'polar', key: 'statusbar.pill.polar' },
    { id: 'dyn', key: 'statusbar.pill.dyn' },
    { id: 'otrack', key: 'statusbar.pill.otrack' },
    { id: 'osnap', key: 'statusbar.pill.osnap' },
    { id: 'space', key: 'statusbar.pill.space' },
    { id: 'units', key: 'statusbar.pill.units' },
    { id: 'transparency', key: 'statusbar.pill.transparency' },
    { id: 'isolate', key: 'statusbar.pill.isolate' },
    { id: 'quickProps', key: 'statusbar.pill.quickProps' },
    { id: 'selFilter', key: 'statusbar.pill.selFilter' },
    { id: 'selCycle', key: 'statusbar.pill.selCycle' },
    { id: 'cleanScreen', key: 'statusbar.pill.cleanScreen' },
    { id: 'annoVisibility', key: 'ui.cad.annotative.visibility' },
    { id: 'annoAutoAdd', key: 'ui.cad.annotative.autoAdd' },
    { id: 'vpScaleSync', key: 'ui.cad.annotative.vpSync' },
];

export function StatusBarCustomizeMenu({ anchor, onClose }: {
    anchor: { x: number; y: number } | null;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const hiddenPills = useStatusBarStore((s) => s.hiddenPills);
    const togglePill = useStatusBarStore((s) => s.togglePill);
    return (
        <PillPopup anchor={anchor} onClose={onClose} width={210}>
            <Box px={3} py={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                {t('statusbar.customizeTitle')}
            </Box>
            <Box maxH="300px" overflowY="auto">
                {STATUS_PILL_LABEL_KEYS.map(({ id, key }) => (
                    <PopupRow
                        key={id}
                        label={t(key)}
                        checked={!hiddenPills.includes(id)}
                        onClick={() => togglePill(id)}
                    />
                ))}
            </Box>
        </PillPopup>
    );
}

// ---------------------------------------------------------------------------
// Small icon-only pills (no popup) — exported as a row for the 2D block
// ---------------------------------------------------------------------------

/** The drafting pill family that toggles plain store flags (Faz 4.1).
 *  `hidden` carries the persisted customize set so individual pills can be
 *  hidden without unmounting the whole row. */
export function DraftingTogglePills({ hidden = [] }: { hidden?: StatusPillId[] }) {
    const { t } = useTranslation();
    const show = (id: StatusPillId) => !hidden.includes(id);
    const showLineweights = useViewStore((s) => s.showLineweights);
    const showTransparency = useViewStore((s) => s.showTransparency);
    const cleanScreen = useViewStore((s) => s.cleanScreen);
    const dynamicInput = useAppStore((s) => s.interactionOptions.dynamicInput);
    const objectSnapTracking = useSnapStore((s) => s.objectSnapTracking);
    const selectionCycling = useSnapStore((s) => s.selectionCycling);
    const quickPropertiesOpen = useAppStore((s) => s.quickPropertiesOpen);

    return (
        <HStack spacing={1} flexShrink={0}>
            {show('lwt') && (
                <IconToggle
                    icon={ArrowLeftRight}
                    on={showLineweights}
                    title={t('statusbar.pill.lwt')}
                    onClick={() => useViewStore.getState().set('showLineweights', !showLineweights)}
                />
            )}
            {show('transparency') && (
                <IconToggle
                    icon={Glasses}
                    on={showTransparency}
                    title={t('statusbar.pill.transparency')}
                    onClick={() => useViewStore.getState().set('showTransparency', !showTransparency)}
                />
            )}
            {show('otrack') && (
                <IconToggle
                    icon={Move3d}
                    on={objectSnapTracking}
                    title={t('statusbar.pill.otrack')}
                    onClick={() => useSnapStore.getState().toggleObjectSnapTracking()}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        useDraftingSettingsStore.getState().openAt('polarTracking');
                    }}
                />
            )}
            {show('selCycle') && (
                <IconToggle
                    icon={ListChecks}
                    on={selectionCycling}
                    title={t('statusbar.pill.selCycle')}
                    onClick={() => useSnapStore.getState().toggleSelectionCycling()}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        useDraftingSettingsStore.getState().openAt('objectSnap');
                    }}
                />
            )}
            {show('quickProps') && (
                <IconToggle
                    icon={Eye}
                    on={quickPropertiesOpen}
                    title={t('statusbar.pill.quickProps')}
                    onClick={() => useAppStore.getState().toggleQuickProperties()}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        useDraftingSettingsStore.getState().openAt('quickProperties');
                    }}
                />
            )}
            {show('dyn') && (
                <IconToggle
                    icon={Pipette}
                    on={dynamicInput}
                    title={t('statusbar.pill.dyn')}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        useDraftingSettingsStore.getState().openAt('dynamicInput');
                    }}
                    onClick={() => {
                        const app = useAppStore.getState();
                        app.setInteractionOption('dynamicInput', !app.interactionOptions.dynamicInput);
                    }}
                />
            )}
            {show('cleanScreen') && (
                <IconToggle
                    icon={PanelTopClose}
                    on={cleanScreen}
                    title={t('statusbar.pill.cleanScreen')}
                    onClick={() => useViewStore.getState().set('cleanScreen', !cleanScreen)}
                />
            )}
        </HStack>
    );
}
